import {AxiosRequestConfig} from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {API, ApiEndpointsEnum} from '../api';
import {IBaseErrorResponse} from './../types/error.types';
import { SendMessageReqBody, UploadAdminChatAttachmentReqBody } from '../types/doctor-admin-chat.types';
import {requestAdminPwaBadgeRefresh} from '../../utils/admin-pwa-badge-event.utils';

const getDoctorAdminMessages = async () => {
  return API.get(ApiEndpointsEnum.GET_DOCTOR_ADMIN_MESSAGES);
}

const useGetDoctorAdminMessage = () => {
  const queryClient = useQueryClient();

  return useQuery(
    ['get-messages'],
    () => getDoctorAdminMessages(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-messages'], {
          refetchActive: false,
        });
      },
    },
  );
};

const getAdminDoctorMessages = async (practitionerId: string) => {
  const url = ApiEndpointsEnum.GET_ADMIN_DOCTOR_MESSAGES.replace(
    '{practitionerId}',
    practitionerId
  );
  const response = await API.get(url);
  return response;
};

// Cached + polled by practitionerId, rather than a plain mutation, so:
// - switching back to a conversation already open this session is instant
//   (served from cache) instead of always re-fetching full history, and
// - react-query dedupes/cancels stale requests for us instead of every
//   caller hand-rolling its own setInterval.
const useGetAdminDoctorMessages = (practitionerId: string) => {
  return useQuery(
    ['admin-doctor-messages', practitionerId],
    () => getAdminDoctorMessages(practitionerId),
    {
      enabled: !!practitionerId,
      refetchInterval: 4000,
    },
  );
};



const sendDoctorAdminMessage = async (reqBody: SendMessageReqBody) => {
  return API.post(`${ApiEndpointsEnum.POST_DOCTOR_ADMIN_MESSAGE}`, reqBody);
};

const useSendDoctorAdminMessage = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (reqBody: SendMessageReqBody) => sendDoctorAdminMessage(reqBody),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['send-message']);
        // The first message to a doctor creates their conversation - refetch
        // the list right away so they appear without waiting for the next
        // poll interval.
        queryClient.invalidateQueries(['get-doctors']);
      },
    }
  );
};

const markAsReadMessage = async (reqBody) => {
  const url = ApiEndpointsEnum.PUT_ADMIN_DOCTOR_MESSAGE_MARKASREAD.replace(
    '{senderType}',
    reqBody.senderType
  ).replace('{practitionerId}', reqBody.practitionerId);
  return API.put(url);
};

const useMarkAsReadMessage = () => {
  return useMutation<any, Error, {senderType: string; practitionerId: string}>(
    reqBody => markAsReadMessage(reqBody),
    {
      onSuccess: requestAdminPwaBadgeRefresh,
    },
  );
};

export const getAdminConversationDoctors = async () => {
  return API.get(ApiEndpointsEnum.GET_ADMIN_CONVERSATION_DOCTORS);
}

const useGetAdminConversationDoctors = () => {
  const queryClient = useQueryClient();

  return useQuery(
    ['get-doctors'],
    () => getAdminConversationDoctors(),
    {
      refetchInterval: 5000,
      onSuccess: () => {
        queryClient.invalidateQueries(['get-doctors'], {
          refetchActive: false,
        });
      },
    },
  );
};

const getAdminConversationDoctor = async (practitionerId: string) => {
  const url = ApiEndpointsEnum.GET_ADMIN_CONVERSATION_DOCTOR.replace(
    '{practitionerId}',
    practitionerId,
  );
  return API.get(url);
};

// Fetches one doctor in the same shape as useGetAdminConversationDoctors,
// regardless of whether a conversation exists yet - lets the sidebar open a
// compose view for a doctor who isn't in the conversation list.
const useGetAdminConversationDoctor = (practitionerId: string) => {
  return useQuery(
    ['get-doctor', practitionerId],
    () => getAdminConversationDoctor(practitionerId),
    {
      enabled: !!practitionerId,
    },
  );
};

const uploadChatAttachment = async (
  reqBody: UploadAdminChatAttachmentReqBody,
) => {
  const headerAuthorization: any = {
    'Content-Type': 'multipart/form-data',
  };

  const config: AxiosRequestConfig = {
    headers: {
      ...headerAuthorization,
    },
  };

  const formData = new FormData();
  formData.append('image', reqBody.image!);

  const url = ApiEndpointsEnum.PUT_ADMIN_DOCTOR_ATTACHMENT_UPLOAD
    .replace("{senderType}", reqBody.senderType)
    .replace("{practitionerId}", reqBody.practitionerId);

  return API.put(url, formData, config);
};

const useUploadChatAttachment = (options) => {
  const queryClient = useQueryClient();

  return useMutation(
    (reqBody: UploadAdminChatAttachmentReqBody) => uploadChatAttachment(reqBody),
    options,
  );
};





export {
  useGetDoctorAdminMessage,
  useSendDoctorAdminMessage,
  useGetAdminConversationDoctors,
  useGetAdminConversationDoctor,
  useGetAdminDoctorMessages,
  useUploadChatAttachment,
  useMarkAsReadMessage
};
